export const PROFILE_SCHEMA_VERSION = 'v1'

export const PROFILE_THEME_VARIABLE_KEYS = [
  '--profile-accent',
  '--profile-bg',
  '--profile-card-bg',
  '--profile-card-opacity',
  '--profile-bg-blur',
  '--profile-text',
  '--profile-font',
  '--profile-radius',
] as const

export type ProfileThemeVariableKey = (typeof PROFILE_THEME_VARIABLE_KEYS)[number]
export type ProfileThemeVariables = Partial<Record<ProfileThemeVariableKey, string>>

const DEFAULT_APP_THEME = {
  surface: 'rgba(9, 12, 16, 0.82)',
  surfaceSolid: 'rgba(9, 12, 16, 1)',
  surfaceVariant: 'rgba(59, 66, 75, 0.8)',
  onSurface: '#f3f0ff',
  primary: '#1d8aff',
  border: 'rgba(255, 255, 255, 0.12)',
}

const SAFE_TEXT_VALUE_PATTERN = /^(?!.*(?:url\s*\(|expression\s*\(|javascript:|data:text\/html|@import|<\/?(?:style|script)))[\s\S]+$/i

function normalizeSafeCssText(value: unknown, maxLength = 200) {
  if (typeof value !== 'string') return null
  const normalized = value.trim()
  if (!normalized || normalized.length > maxLength) return null
  if (!SAFE_TEXT_VALUE_PATTERN.test(normalized)) return null
  return normalized
}

function normalizeBoundedNumber(value: unknown, { min, max, suffix = '' }: { min: number; max: number; suffix?: string }) {
  const raw = typeof value === 'number' ? String(value) : typeof value === 'string' ? value.trim() : ''
  if (!raw) return null

  const normalized = suffix && raw.toLowerCase().endsWith(suffix) ? raw.slice(0, -suffix.length).trim() : raw
  if (!/^-?\d+(?:\.\d+)?$/.test(normalized)) return null

  const numeric = Number(normalized)
  if (!Number.isFinite(numeric) || numeric < min || numeric > max) return null

  if (!suffix) return String(numeric)
  const serialized = Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(2).replace(/0+$/, '').replace(/\.$/, '')
  return `${serialized}${suffix}`
}

export function validateAndNormalizeProfileThemeVariables(input: unknown) {
  if (input == null) {
    return {
      themeVariables: {} as ProfileThemeVariables,
      errors: [] as string[],
    }
  }

  if (typeof input !== 'object' || Array.isArray(input)) {
    return {
      themeVariables: {} as ProfileThemeVariables,
      errors: ['Theme variables must be an object.'],
    }
  }

  const safeInput = input as Record<string, unknown>
  const themeVariables: ProfileThemeVariables = {}
  const errors: string[] = []

  for (const [key, value] of Object.entries(safeInput)) {
    if (!PROFILE_THEME_VARIABLE_KEYS.includes(key as ProfileThemeVariableKey)) {
      errors.push(`Theme variable ${key} is not supported.`)
      continue
    }

    let normalized: string | null = null

    if (key === '--profile-card-opacity') {
      normalized = normalizeBoundedNumber(value, { min: 0, max: 1 })
    } else if (key === '--profile-bg-blur') {
      normalized = normalizeBoundedNumber(value, { min: 0, max: 40, suffix: 'px' })
    } else if (key === '--profile-radius') {
      normalized = normalizeBoundedNumber(value, { min: 0, max: 64, suffix: 'px' })
    } else {
      normalized = normalizeSafeCssText(value, key === '--profile-font' ? 160 : 200)
    }

    if (!normalized) {
      errors.push(`Theme variable ${key} has an invalid value.`)
      continue
    }

    themeVariables[key as ProfileThemeVariableKey] = normalized
  }

  return { themeVariables, errors }
}

function readRootVar(name: string, fallback: string) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return fallback
  const value = window.getComputedStyle(document.documentElement).getPropertyValue(name).trim()
  return value || fallback
}

export function collectAppProfileTheme() {
  return {
    surface: readRootVar('--theme-surface', DEFAULT_APP_THEME.surface),
    surfaceSolid: readRootVar('--theme-surface-solid', DEFAULT_APP_THEME.surfaceSolid),
    surfaceVariant: readRootVar('--theme-surface-variant', DEFAULT_APP_THEME.surfaceVariant),
    onSurface: readRootVar('--theme-on-surface', DEFAULT_APP_THEME.onSurface),
    primary: readRootVar('--theme-primary', DEFAULT_APP_THEME.primary),
    border: readRootVar('--theme-border', DEFAULT_APP_THEME.border),
  }
}

export function resolveProfileThemeVariables(overrides: unknown, appTheme = collectAppProfileTheme()) {
  const { themeVariables } = validateAndNormalizeProfileThemeVariables(overrides)

  return {
    '--profile-accent': appTheme.primary,
    '--profile-bg': appTheme.surfaceSolid,
    '--profile-card-bg': appTheme.surface,
    '--profile-card-opacity': '1',
    '--profile-bg-blur': '0px',
    '--profile-text': appTheme.onSurface,
    '--profile-font': "'Inter', system-ui, sans-serif",
    '--profile-radius': '20px',
    ...themeVariables,
  } satisfies Record<ProfileThemeVariableKey, string>
}

export function buildProfileThemeCssVariables(overrides: unknown, appTheme = collectAppProfileTheme()) {
  const resolved = resolveProfileThemeVariables(overrides, appTheme)
  return Object.entries(resolved)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n')
}
